import { memo, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { estadoDoAcervo } from './voltaAoAcervo';
import {
  Archive,
  Check,
  CheckSquare,
  Copy,
  ExternalLink,
  Eye,
  FileText,
  FolderOpen,
  History,
  MoreVertical,
  Pencil,
  Share2,
  UserCheck,
} from 'lucide-react';

import { eDeColega, podePreparar, type Processo } from '@/api/client';
import { DataTablePagination } from '@/components/DataTablePagination';
import { SeloDeAdvogados } from '@/components/escritorio/SeloDeAdvogados';
import { useCompartilharNoEscritorio } from '@/components/escritorio/useCompartilharNoEscritorio';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { CopyButton } from '@/components/ui/copy-button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Spinner } from '@/components/ui/spinner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useNotification } from '@/context/NotificationProvider';
import { abrirAutosEmNovaAba } from '@/lib/slim';
import { cliqueDoMeioNaLinha, useAbrirAutos } from '@/hooks/useAbrirAutos';
import { formatPartesResumo } from '@/lib/partes/resumo';
import { useAtuacao, usePodeColaborar } from '@/lib/delegacao/atuacao';

import { TagsDoRegistro } from '@/components/tags/TagsDoRegistro';
import { TagChips } from '@/components/ui/tag-chip';
import { CACHES_DE_PROCESSO, useTagsNoCache } from '@/hooks/useTagsNoCache';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useToqueLongo } from '@/hooks/useToqueLongo';
import { usePeticionarNoProcesso } from '@/hooks/useAcoesNoProcesso';
import { cn } from '@/lib/utils';

import { AudienciaNaLista, VencimentoNaLista } from './ColunasDoAcervo';
import { FavoritoProcessoButton } from './FavoritoProcessoButton';
import { buscaDoProtocolo } from '@/pages/processos/autos/protocolo/entradaDoProtocolo';

const TAMANHOS = [25, 50, 100];
const TAMANHO_PADRAO = 50;

// --- Props ---

interface ProcessoTableProps {
  processos: Processo[] | undefined;
  isLoading: boolean;
  onEdit: (processo: Processo) => void;
  onArchive: (processo: Processo) => void;
  /** Seleção para tags em lote (PLANO_TAGS D13). */
  selecionados: Set<string>;
  onSelecionadosChange: (ids: Set<string>) => void;
  /** O processo cujo diálogo de tags está aberto (nulo ao fechar). */
  onTagsAbertas?: (id: string | null) => void;
  /** A página (1…), que a tela do acervo guarda na URL para o "Voltar" dos autos. */
  pagina?: number;
  onPaginaChange?: (pagina: number) => void;
  /** O Gestor (§19): "Quem cuida…" em todo processo, e o de colega também se seleciona. */
  onDistribuir?: (processo: Processo) => void;
  /** Quais o Gestor distribui (o de colega de outro escritório, não). */
  distribuivel?: (processo: Processo) => boolean;
  /** O número buscado está no acervo, mas fora do "Quem cuida" escolhido: a tabela vazia diz isso. */
  foraDoFiltro?: { filtro: string; onVerTodos: () => void };
}

/**
 * O que as linhas fazem — um objeto ESTÁVEL entre redesenhos (lê as props mais recentes
 * por ref). Assim cada linha (`memo`) só se redesenha quando muda o processo dela ou a
 * marcação dela: marcar um processo num acervo de centenas redesenhava a lista inteira,
 * e o botão do lote demorava a aparecer (1,4 s com 200 processos, medido no jsdom).
 */
/** O clique que pediu — Ctrl, ⌘ ou o botão do meio levam os autos a outra aba. */
type EventoDeClique = { ctrlKey?: boolean; metaKey?: boolean; button?: number; preventDefault?: () => void };

interface RowActions {
  open: (numero: string, evento?: EventoDeClique) => void;
  openInNewTab: (numero: string) => void;
  petition: (numero: string) => void;
  movements: (numero: string, evento?: EventoDeClique) => void;
  dossier: (numero: string) => void;
  edit: (processo: Processo) => void;
  archive: (processo: Processo) => void;
  toggle: (id: string) => void;
  /** Copia o número (no celular, sai do cartão para o ⋮). */
  copy: (numero: string) => void;
  tagsOpened: (id: string | null) => void;
  /** "Compartilhar no escritório…" (4c); ausente para quem não é de um escritório. */
  share?: (processo: Processo) => void;
  /** "Quem cuida…" (§19); só para o Gestor, e só no que ele distribui. */
  distribute?: (processo: Processo) => void;
  distributable: (processo: Processo) => boolean;
  /** Prepara (tags, seleção para tags) neste processo: o próprio, ou o do colega em Colaboração. */
  prepares: (processo: Processo) => boolean;
}

export function ProcessoTable({
  processos,
  isLoading,
  onEdit,
  onArchive,
  selecionados,
  onSelecionadosChange,
  onTagsAbertas,
  pagina = 1,
  onPaginaChange,
  onDistribuir,
  distribuivel,
  foraDoFiltro,
}: ProcessoTableProps) {
  const navigate = useNavigate();
  const podeColaborar = usePodeColaborar();
  // Editar o cadastro do processo fica com o titular (cópia local de dado do tribunal).
  const atuacao = useAtuacao();
  const podeEditar = podeColaborar && !atuacao;
  const [searchParams] = useSearchParams();
  // A busca e o filtro de vencimento vão junto, para o "Voltar" dos autos.
  const voltaAoAcervo = estadoDoAcervo(searchParams);
  const { showError, showSuccess } = useNotification();
  const tagsNoCache = useTagsNoCache(CACHES_DE_PROCESSO);
  const compartilhar = useCompartilharNoEscritorio();
  const { podeCompartilhar, abrir: abrirCompartilhar } = compartilhar;

  // Os autos na mesma aba ou em outra (Ctrl+clique, botão do meio, preferência).
  const abrirAutos = useAbrirAutos();
  const latest = useRef({ navigate, abrirAutos, voltaAoAcervo, onEdit, onArchive, selecionados, onSelecionadosChange, onTagsAbertas, showError, showSuccess, onDistribuir });
  // Depois de cada render, antes de qualquer clique: os handlers leem sempre o atual.
  useLayoutEffect(() => {
    latest.current = { navigate, abrirAutos, voltaAoAcervo, onEdit, onArchive, selecionados, onSelecionadosChange, onTagsAbertas, showError, showSuccess, onDistribuir };
  });
  const distribui = !!onDistribuir;
  const rowActions = useMemo<RowActions>(
    () => ({
      open: (numero, evento) => latest.current.abrirAutos(numero, { state: latest.current.voltaAoAcervo, evento }),
      // Aba dedicada por processo: trabalhar em vários ao mesmo tempo sem perder o
      // que está aberto aqui. Bloqueio do navegador é avisado — clique sem resposta
      // parece bug.
      openInNewTab: (numero) => {
        if (!abrirAutosEmNovaAba(numero)) {
          latest.current.showError('O navegador bloqueou a nova aba. Autorize os pop-ups deste site.');
        }
      },
      // Peticionar é nos autos: o editor e a barra do protocolo (PLANO_BANCADA, Bloco 4).
      petition: (numero) => latest.current.abrirAutos(numero, { search: buscaDoProtocolo() }),
      movements: (numero, evento) =>
        latest.current.abrirAutos(numero, {
          search: '?view=movimentos',
          state: latest.current.voltaAoAcervo,
          evento,
        }),
      dossier: (numero) => latest.current.navigate(`/app/processos/${numero}/dossie`),
      edit: (processo) => latest.current.onEdit(processo),
      archive: (processo) => latest.current.onArchive(processo),
      toggle: (id) => {
        const next = new Set(latest.current.selecionados);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        latest.current.onSelecionadosChange(next);
      },
      copy: (numero) => {
        void navigator.clipboard
          .writeText(numero)
          .then(() => latest.current.showSuccess('Número copiado'))
          .catch(() => latest.current.showError('Não foi possível copiar o número.'));
      },
      tagsOpened: (id) => latest.current.onTagsAbertas?.(id),
      share: podeCompartilhar
        ? (processo) => abrirCompartilhar('PROCESSO', processo.id, `Processo ${processo.numero}`)
        : undefined,
      distribute: distribui ? (processo) => latest.current.onDistribuir?.(processo) : undefined,
      distributable: (processo) => distribui && (distribuivel?.(processo) ?? true),
      prepares: (processo) => podePreparar(processo),
    }),
    [podeCompartilhar, abrirCompartilhar, distribui, distribuivel],
  );

  // Uma página por vez: com mil processos, desenhar a lista inteira travava a tela (§18).
  // A lista inteira continua no cache (filtros e offline são no cliente); a página vem da
  // tela do acervo, que a guarda na URL e volta à 1 quando os filtros mudam.
  const [tamanho, setTamanho] = useState(TAMANHO_PADRAO);
  const topo = useRef<HTMLDivElement>(null);
  const total = processos?.length ?? 0;
  const paginas = Math.max(1, Math.ceil(total / tamanho));
  const atual = Math.min(Math.max(1, pagina), paginas);
  const daPagina = (processos ?? []).slice((atual - 1) * tamanho, atual * tamanho);
  // No celular, "Carregar mais" no lugar das páginas (docs/PLANO_RESPONSIVIDADE_MOBILE.md §3.4):
  // a lista mostra da 1ª até a página atual. A página da URL vira "até onde carreguei", e o
  // "Voltar" dos autos devolve a lista do mesmo tamanho.
  const noCelular = !useMediaQuery('(min-width: 640px)');
  const doCartao = noCelular ? (processos ?? []).slice(0, atual * tamanho) : daPagina;
  const irPara = (numero: number) => {
    onPaginaChange?.(numero);
    // O controle fica no rodapé: a página nova começa do topo.
    topo.current?.scrollIntoView?.({ block: 'start' });
  };

  // Tags em lote só no próprio acervo: o processo de um colega é só para ver (2c) — salvo
  // para o Gestor, que o distribui (§19). A seleção de todos é a da página: marcar o que
  // não está desenhado surpreendia.
  // Selecionável: o próprio, o do colega em que colabora (tags, Bloco 3) e o que o Gestor distribui.
  const visiveis = daPagina.filter(
    (p) =>
      podePreparar(p) || (distribui && (distribuivel?.(p) ?? true)),
  );
  const todosMarcados = visiveis.length > 0 && visiveis.every((p) => selecionados.has(p.id));
  const alternarTodos = () => {
    const proximos = new Set(selecionados);
    for (const p of visiveis) {
      if (todosMarcados) proximos.delete(p.id);
      else proximos.add(p.id);
    }
    onSelecionadosChange(proximos);
  };

  return (
    <>
    <div ref={topo} className="scroll-mt-20" />
    {/* No celular sem a moldura: os cartões já têm a deles, e a de fora comia 24px de cada lado. */}
    <Card className="border-0 bg-transparent shadow-none sm:border sm:bg-card">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <Spinner size={32} label="Carregando processos" />
            </div>
          ) : processos && processos.length > 0 ? (
            <>
              {/* Mobile card view */}
              <div className="sm:hidden space-y-2">
                {doCartao.map((processo) => (
                  <CartaoDoProcesso
                    key={processo.id}
                    processo={processo}
                    marcada={selecionados.has(processo.id)}
                    emSelecao={selecionados.size > 0}
                    podeColaborar={podeColaborar}
                    podeEditar={podeEditar}
                    actions={rowActions}
                    tagsNoCache={tagsNoCache}
                  />
                ))}
              </div>

              {/* Desktop table view */}
              <div className="hidden sm:block">
                {/* Layout fixo: a coluna Processo fica com o resto e a linha de partes corta
                    com reticências, em vez de esticar a tabela e empurrar o "⋮" para fora. */}
                <Table className="table-fixed">
                  <TableHeader>
                    <TableRow>
                      {podeColaborar && (
                        <TableHead className="w-10">
                          <CaixaSelecao marcada={todosMarcados} onAlternar={alternarTodos} rotulo="Selecionar os processos desta página" />
                        </TableHead>
                      )}
                      <TableHead className="w-10 px-1">
                        <span className="sr-only">Favorito</span>
                      </TableHead>
                      <TableHead>Processo</TableHead>
                      <TableHead className="w-12">
                        <span className="sr-only">Ações</span>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {daPagina.map((processo) => (
                      <LinhaDoProcesso
                        key={processo.id}
                        processo={processo}
                        marcada={selecionados.has(processo.id)}
                        podeColaborar={podeColaborar}
                        podeEditar={podeEditar}
                        actions={rowActions}
                        tagsNoCache={tagsNoCache}
                      />
                    ))}
                  </TableBody>
                </Table>
              </div>
              {noCelular && atual < paginas && (
                <Button
                  variant="outline"
                  className="mt-3 h-11 w-full sm:hidden"
                  onClick={() => onPaginaChange?.(atual + 1)}
                >
                  Carregar mais ({total - atual * tamanho} {total - atual * tamanho === 1 ? 'restante' : 'restantes'})
                </Button>
              )}
              {!noCelular && total > TAMANHOS[0] && (
                <DataTablePagination
                  page={atual}
                  pageSize={tamanho}
                  total={total}
                  pages={paginas}
                  pageSizeOptions={TAMANHOS}
                  onPageChange={irPara}
                  onPageSizeChange={(novo) => {
                    setTamanho(novo);
                    irPara(1);
                  }}
                />
              )}
            </>
          ) : foraDoFiltro ? (
            <div className="p-12 text-center">
              <h6 className="mb-2 text-base font-semibold text-muted-foreground">
                Processo fora de “{foraDoFiltro.filtro}” em Quem cuida
              </h6>
              <p className="mb-4 text-sm text-muted-foreground">Ele está no acervo, sob os cuidados de outra pessoa.</p>
              <Button variant="outline" onClick={foraDoFiltro.onVerTodos}>
                Ver em Todos
              </Button>
            </div>
          ) : (
            <div className="p-12 text-center">
              <h6 className="mb-2 text-base font-semibold text-muted-foreground">
                Nenhum processo encontrado
              </h6>
              <p className="text-sm text-muted-foreground">
                Tente ajustar os filtros ou adicione um novo processo
              </p>
            </div>
          )}
    </Card>
    {compartilhar.dialogo}
    </>
  );
}

interface LinhaProps {
  processo: Processo;
  marcada: boolean;
  podeColaborar: boolean;
  podeEditar: boolean;
  actions: RowActions;
  tagsNoCache: ReturnType<typeof useTagsNoCache>;
}

/**
 * Celular: o processo em cartão (docs/PLANO_RESPONSIVIDADE_MOBILE.md §3.5). Em cima, o número e,
 * à direita, a estrela e o ⋮ (44px cada); embaixo, partes, tribunal e uma linha com o que vence,
 * a audiência e as tags. Copiar o número fica no ⋮. A caixa de seleção só aparece no modo
 * seleção, em que se entra pelo toque longo (ou "Selecionar" no ⋮); nele, o toque marca.
 */
const CartaoDoProcesso = memo(function CartaoDoProcesso({
  processo,
  marcada,
  emSelecao,
  podeColaborar,
  podeEditar,
  actions,
  tagsNoCache,
}: LinhaProps & { emSelecao: boolean }) {
  const partesResumo = formatPartesResumo(processo.partes_resumo);
  const doColega = eDeColega(processo);
  const selecionavel = podeColaborar && (actions.prepares(processo) || actions.distributable(processo));
  const toque = useToqueLongo(() => actions.toggle(processo.id), selecionavel);
  return (
    <Card
      className={cn('cursor-pointer select-none border', marcada && 'border-primary/60 bg-primary/5')}
      {...toque.handlers}
      onClick={(e) => {
        if (toque.consumiuClique()) return;
        if (emSelecao && selecionavel) actions.toggle(processo.id);
        else actions.open(processo.numero, e);
      }}
    >
      <div className="flex items-start gap-0.5 py-0.5 pl-3 pr-0.5">
        {emSelecao && selecionavel && (
          <span className="-ml-2">
            <CaixaSelecao
              marcada={marcada}
              onAlternar={() => actions.toggle(processo.id)}
              rotulo={`Selecionar ${processo.numero}`}
            />
          </span>
        )}
        <div className="min-w-0 flex-1 py-2.5">
          <span className="block text-sm font-medium tabular-nums [overflow-wrap:anywhere]">{processo.numero}</span>
          <SeloDeAdvogados advogados={processo.advogados} acesso={processo.acesso} />
          <QuemCuida processo={processo} />
        </div>
        {!doColega && (
          // Já tem 44px: sem a faixa de toque extra (`::after`), que cobria a borda do ⋮ ao lado.
          <FavoritoProcessoButton processoId={processo.id} favorito={!!processo.favorito} className="h-11 w-11 after:hidden" />
        )}
        <MenuDoProcesso
          processo={processo}
          podeColaborar={podeColaborar}
          podeEditar={podeEditar}
          actions={actions}
          noCartao={{ selecionavel, marcada }}
        />
      </div>
      <div className="space-y-1 px-3 pb-3">
        {partesResumo && <p className="break-words text-xs text-muted-foreground">{partesResumo}</p>}
        <p className="break-words text-xs text-muted-foreground">
          {processo.tribunal} &middot; {processo.classe}
        </p>
        <div className="flex flex-wrap items-center gap-1.5 empty:hidden">
          <VencimentoNaLista vencimento={processo.vencimento} />
          <AudienciaNaLista audiencia={processo.audiencia} />
          <TagsDaLinha processo={processo} tagsNoCache={tagsNoCache} actions={actions} max={3} />
        </div>
      </div>
    </Card>
  );
});

/** Computador: o processo em linha da tabela. */
const LinhaDoProcesso = memo(function LinhaDoProcesso({
  processo,
  marcada,
  podeColaborar,
  podeEditar,
  actions,
  tagsNoCache,
}: LinhaProps) {
  const partesResumo = formatPartesResumo(processo.partes_resumo);
  const doColega = eDeColega(processo);
  return (
    <TableRow
      onClick={(e) => actions.open(processo.numero, e)}
      onMouseDown={(e) => cliqueDoMeioNaLinha(e) && e.preventDefault()}
      onAuxClick={(e) => cliqueDoMeioNaLinha(e) && actions.open(processo.numero, e)}
      className="cursor-pointer group/row"
    >
      {podeColaborar && (
        <TableCell className="w-10">
          {(actions.prepares(processo) || actions.distributable(processo)) && (
            <CaixaSelecao
              marcada={marcada}
              onAlternar={() => actions.toggle(processo.id)}
              rotulo={`Selecionar ${processo.numero}`}
            />
          )}
        </TableCell>
      )}
      <TableCell className="w-10 px-1">
        {/* A estrela é o "acompanho de perto" do dono: na linha de um colega, nada. */}
        {!doColega && <FavoritoProcessoButton processoId={processo.id} favorito={!!processo.favorito} />}
      </TableCell>
      <TableCell className="min-w-0">
        {/* A primeira linha é o cabeçalho do processo: o número e, à direita, o que vence,
            a próxima audiência e as tags. Embaixo, classe · partes e o tribunal. Tudo numa
            coluna só: com uma coluna para cada, o "⋮" saía da tela em monitor pequeno. */}
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="flex items-center gap-1">
            <span className="text-sm font-medium">{processo.numero}</span>
            {/* Só o botão de copiar retém o clique — a linha
                inteira é o caminho para os autos. */}
            <span onClick={(e) => e.stopPropagation()}>
              <CopyButton
                text={processo.numero}
                label="Copiar número"
                className="h-6 w-6 opacity-0 group-hover/row:opacity-100 transition-opacity"
              />
            </span>
          </span>
          {/* À direita, juntos: o que vence, a próxima audiência e as tags. */}
          <span className="ml-auto flex flex-wrap items-center justify-end gap-1.5">
            <VencimentoNaLista vencimento={processo.vencimento} />
            <AudienciaNaLista audiencia={processo.audiencia} />
            <TagsDaLinha processo={processo} tagsNoCache={tagsNoCache} actions={actions} max={3} />
          </span>
        </div>
        <SeloDeAdvogados advogados={processo.advogados} acesso={processo.acesso} className="mt-0.5" />
        <QuemCuida processo={processo} />
        {(processo.classe || partesResumo) && (
          <span className="mt-0.5 block truncate text-xs">
            {processo.classe}
            {processo.classe && partesResumo && <span className="text-muted-foreground"> · </span>}
            {partesResumo && <span className="text-muted-foreground">{partesResumo}</span>}
          </span>
        )}
        {processo.tribunal && <span className="block text-[11px] text-muted-foreground">{processo.tribunal}</span>}
      </TableCell>
      <TableCell className="w-12 text-right">
        <MenuDoProcesso processo={processo} podeColaborar={podeColaborar} podeEditar={podeEditar} actions={actions} />
      </TableCell>
    </TableRow>
  );
});

/** Quem cuida do processo no escritório, quando foi distribuído (§19); sem isso, o dono. */
function QuemCuida({ processo }: { processo: Processo }) {
  const rs = processo.responsaveis ?? [];
  if (rs.length === 0) return null;
  const nomes = rs.map((r) => (r.tipo === 'EQUIPE' ? `Equipe ${r.nome}` : r.nome));
  return (
    <span className="mt-0.5 block text-[11px] text-muted-foreground [overflow-wrap:anywhere]">
      Cuida: {nomes.join(', ')}
    </span>
  );
}

/** O menu "⋮" do processo — o mesmo no cartão e na linha. */
function MenuDoProcesso({
  processo,
  podeColaborar,
  podeEditar,
  actions,
  noCartao,
}: Pick<LinhaProps, 'processo' | 'podeColaborar' | 'podeEditar' | 'actions'> & {
  /** No cartão do celular, o ⋮ também copia o número e entra no modo seleção. */
  noCartao?: { selecionavel: boolean; marcada: boolean };
}) {
  // No processo de um colega: quem colabora peticiona (ou prepara) e vê o dossiê; quem cuida
  // dele e o gestor editam os dados e arquivam (o arquivar é o status "Encerrado"), como o
  // servidor diz em ``pode_editar``. Compartilhar é só do dono.
  const proprio = !eDeColega(processo);
  const acoes = usePeticionarNoProcesso(processo);
  const editaOsDados = processo.pode_editar !== false;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
        <Button
          variant="ghost"
          size="icon"
          aria-label={`Ações do processo ${processo.numero}`}
          className={cn('text-muted-foreground', noCartao ? 'h-11 w-11' : 'h-8 w-8')}
        >
          <MoreVertical className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      {/* O conteúdo vai para um portal, mas o clique ainda sobe pela árvore do React até a
          linha — que abre os autos. Sem isto, "Editar", "Arquivar" e "Dossiê" levavam
          aos autos junto. */}
      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
        <DropdownMenuItem onClick={() => actions.open(processo.numero)}>
          <Eye className="mr-3 h-4 w-4" /> Visualizar
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => actions.openInNewTab(processo.numero)}>
          <ExternalLink className="mr-3 h-4 w-4" /> Abrir em nova aba
        </DropdownMenuItem>
        {noCartao && (
          <DropdownMenuItem onClick={() => actions.copy(processo.numero)}>
            <Copy className="mr-3 h-4 w-4" /> Copiar número
          </DropdownMenuItem>
        )}
        {noCartao?.selecionavel && (
          <DropdownMenuItem onClick={() => actions.toggle(processo.id)}>
            <CheckSquare className="mr-3 h-4 w-4" /> {noCartao.marcada ? 'Desmarcar' : 'Selecionar'}
          </DropdownMenuItem>
        )}
        {podeEditar && editaOsDados && (
          <DropdownMenuItem onClick={() => actions.edit(processo)}>
            <Pencil className="mr-3 h-4 w-4" /> Editar processo
          </DropdownMenuItem>
        )}
        {podeColaborar && acoes.podePeticionar && (
          <DropdownMenuItem onClick={() => actions.petition(processo.numero)}>
            <FileText className="mr-3 h-4 w-4" /> {acoes.rotuloPeticionar}
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={() => actions.movements(processo.numero)}>
          <History className="mr-3 h-4 w-4" /> Ver movimentações
        </DropdownMenuItem>
        {proprio && actions.share && (
          <DropdownMenuItem onClick={() => actions.share?.(processo)}>
            <Share2 className="mr-3 h-4 w-4" /> Compartilhar no escritório…
          </DropdownMenuItem>
        )}
        {actions.distribute && actions.distributable(processo) && (
          <DropdownMenuItem onClick={() => actions.distribute?.(processo)}>
            <UserCheck className="mr-3 h-4 w-4" /> Quem cuida…
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={() => actions.dossier(processo.numero)}>
          <FolderOpen className="mr-3 h-4 w-4" /> Dossiê
        </DropdownMenuItem>
        {podeEditar && editaOsDados && (
          <DropdownMenuItem
            onClick={() => actions.archive(processo)}
            className="text-destructive focus:text-destructive"
          >
            <Archive className="mr-3 h-4 w-4" /> Arquivar
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * As tags da linha. No próprio processo, as editáveis (TagsDoRegistro) e, ao lado, as que
 * colegas aplicaram nas linhas deles do mesmo CNJ, só para ver. No de um colega, todas só
 * para ver: o PUT de tags substituiria as da linha dele (escrita é o Bloco 3).
 */
function TagsDaLinha({
  processo,
  tagsNoCache,
  actions,
  max,
}: Pick<LinhaProps, 'processo' | 'tagsNoCache' | 'actions'> & { max: number }) {
  const outras = processo.tags_outras_linhas ?? [];
  // No do colega em Leitura, as tags só se veem; em Colaboração, edita as que vê (Bloco 3).
  if (!actions.prepares(processo)) {
    const todas = [...(processo.tags ?? []), ...outras];
    return todas.length > 0 ? <TagChips tags={todas} max={max} /> : null;
  }
  return (
    <>
      <TagsDoRegistro
        recurso="processos"
        registroId={processo.id}
        identificador={processo.numero}
        tags={processo.tags}
        noCache={tagsNoCache}
        onAbertoChange={(aberto) => actions.tagsOpened(aberto ? processo.id : null)}
        max={max}
      />
      {outras.length > 0 && <TagChips tags={outras} max={max} className="opacity-70" />}
    </>
  );
}

/** Caixa de seleção da linha (alvo de toque de 44 px no celular). Não abre os autos. */
function CaixaSelecao({ marcada, onAlternar, rotulo }: { marcada: boolean; onAlternar: () => void; rotulo: string }) {
  return (
    <Button
      variant="ghost"
      size="icon"
      role="checkbox"
      aria-checked={marcada}
      aria-label={rotulo}
      onClick={(e) => {
        e.stopPropagation();
        onAlternar();
      }}
      className="h-11 w-11 shrink-0 sm:h-8 sm:w-8"
    >
      <span
        className={cn(
          'flex h-4 w-4 items-center justify-center rounded border',
          marcada ? 'border-primary bg-primary text-primary-foreground' : 'border-muted-foreground/50',
        )}
        aria-hidden
      >
        {marcada && <Check className="h-3 w-3" />}
      </span>
    </Button>
  );
}
